'use client'

import { Icon } from '@/components/ui/icon';
import { IconName } from '@/config/icons';
import { FooterNavigation, FooterNavigationItem } from '@/config/nav/footer-navigation';
import { ChoiceLogo } from '@/media/choice-graphics-logo';
import { ChoiceMark } from '@/media/choice-graphics-mark';
import { Link, cn } from "@heroui/react";
import { CookiePreferencesLink } from '../global-footer/cookie-preferences-link';
import { FooterColumn } from './column';

export function AppFooter({ ecommerceEnabled = false }: { ecommerceEnabled?: boolean } = {}) {

  return (
    <footer
      className={cn(
        'w-full mt-12 pb-[calc(var(--app-bottom-nav-h,0px)+0.5rem)] md:pb-0',
        // Dark chrome, matching the header: the footer bookends the page in
        // the same colour as the header bar. That was plum #242038 from the
        // 2026-09-08 brand-recolour handoff (which had already retired sky
        // from this role); the purple came out of the palette on 2026-09-09
        // and the surface is the site ink now.
        'bg-[color:var(--cw-chrome)] text-[color:var(--cw-chrome-fg)]'
      )}
    >
      <div className='container px-4 m-auto sm:px-6'>
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 gap-8 py-8 md:py-12">
          {/* Logo and Social Links */}
          <div className="space-y-4">
            <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-center">
              {/* The lockup is real text, so it and the sr-only <h1> below
                  would otherwise announce the brand twice. The <h1> stays as
                  the announced name and document structure; the visible mark
                  is decorative to assistive tech.

                  The old `block py-2` shaped the retired SVG and is gone: the
                  lockup owns its own display mode (`block` flattened its two
                  lines onto one) and the row's gap-4 supplies the spacing. */}
              <ChoiceMark className="h-8 sm:h-9" />
              <ChoiceLogo
                size="xl"
                orientation="inline"
                aria-hidden="true"
                style={{ '--cw-logo-primary': 'var(--cw-chrome-fg)', '--cw-logo-secondary': 'var(--cw-chrome-fg)' } as React.CSSProperties}
              />
              <h1 className="text-2xl font-bold sr-only text-[color:var(--cw-chrome-fg)]">Choice Graphics</h1>
            </div>
            <p className="w-full text-center sm:text-left text-small text-[color:var(--cw-chrome-fg)]/70 sm:w-1/2">
              Your premier destination for vehicle wraps, graphics, and
              signage solutions. Quality service since 2010.
            </p>
            <div className="flex justify-center mt-6 space-x-6 sm:justify-start">
              {FooterNavigation.items.social.map((item: FooterNavigationItem) => (
                <Link
                  key={item.name}
                  isExternal
                  className={cn(
                    'flex items-center justify-center w-9 h-9 rounded-full border transition-opacity hover:opacity-70',
                    'border-[rgba(230,234,239,.22)] text-[color:var(--cw-chrome-fg)]'
                  )}
                  href={item.href}
                  title={item.name}
                >
                  <span className="sr-only">{item.name}</span>
                  <Icon icon={item.icon as IconName} className="w-4 h-4" aria-hidden="true" />
                </Link>
              ))}
            </div>
          </div>

          {/* Navigation Columns */}
          <div className="flex flex-row flex-wrap items-stretch gap-8 md:justify-end">
            <FooterColumn
              title="Services"
              items={FooterNavigation.items.services}
            />
            <FooterColumn
              title="Fleet Wraps"
              items={FooterNavigation.items.support}
            />
            <FooterColumn
              title="Company"
              items={FooterNavigation.items.about.filter(item => ecommerceEnabled || item.href !== '/products')}
              className="col-span-2 sm:col-span-1"
            />
          </div>
        </div>

        {/* Legal Links and Copyright */}
        <div className="flex flex-col items-center justify-between py-6 space-y-4 md:flex-row md:space-y-0 border-t border-[rgba(230,238,239,.16)]">
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 md:justify-start">
            {FooterNavigation.items.legal.map((item: FooterNavigationItem) => (
              <Link
                key={item.name}
                href={item.href}
                className="text-xs transition-opacity text-[color:var(--cw-chrome-fg)]/60 hover:opacity-70"
              >
                {item.name}
              </Link>
            ))}
            <CookiePreferencesLink className="text-xs transition-opacity text-[color:var(--cw-chrome-fg)]/60 hover:opacity-70" />
          </div>
          <p className="text-xs leading-5 text-[color:var(--cw-chrome-fg)]/60">
            &copy; {new Date().getFullYear()} Choice OMG. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}
